import UserServices from '@/services/users-services';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

/** Per-mode PAYG pack balances */
export interface PackBalances {
  standard: number;
  full: number;
  deep: number;
}

// Credit balance state — sourced from /usage endpoint (payg.balance)
interface CreditsState {
  balances: PackBalances | null;
  planName: string | null;
  isPaidUser: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: CreditsState = {
  balances: null,
  planName: null,
  isPaidUser: false,
  loading: false,
  error: null,
};

// Async thunk that fetches credit balance from the /usage endpoint.
// The backend returns payg.balance as per-mode pack balances.
// Falls back to zadanie.packs.balance (old single-balance format) for backward compat.
export const fetchCreditBalance = createAsyncThunk(
  'credits/fetchCreditBalance',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await UserServices.getUserUsage();

      if (response.status === 'success' && response.data) {
        const { zadanie, is_paid_user } = response.data;

        // Prefer new per-mode balances from backend
        const paygBalance = response.data.payg?.balance;

        let balances: PackBalances | null;
        if (paygBalance) {
          balances = {
            standard: paygBalance.standard ?? 0,
            full: paygBalance.full ?? 0,
            deep: paygBalance.deep ?? 0,
          };
        } else {
          // Backward compat: distribute old single balance equally to standard
          const legacyBalance = zadanie?.packs?.balance ?? 0;
          balances =
            legacyBalance > 0
              ? { standard: legacyBalance, full: 0, deep: 0 }
              : { standard: 0, full: 0, deep: 0 };
        }

        return {
          balances,
          planName: zadanie?.plan?.name ?? null,
          isPaidUser: is_paid_user,
        };
      } else {
        return rejectWithValue(
          response.message || 'Failed to fetch credit balance'
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while fetching credit balance';
      return rejectWithValue(errorMessage);
    }
  }
);

const creditsSlice = createSlice({
  name: 'credits',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreditBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCreditBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balances = action.payload.balances;
        state.planName = action.payload.planName;
        state.isPaidUser = action.payload.isPaidUser;
        state.error = null;
      })
      .addCase(fetchCreditBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default creditsSlice.reducer;
